import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Download, Loader2, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import MANUAL from '@/data/manualContent';

const SECTIONS = Object.entries(MANUAL);

export default function UserManualPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get('/manual/pdf-data');
      const byteChars = atob(data.pdf_base64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename || 'RTR_User_Manual.pdf';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert('Failed to generate PDF: ' + (e?.response?.data?.detail || e.message));
    }
    setDownloading(false);
  };

  const toggle = (key) => setExpanded(expanded === key ? null : key);

  return (
    <div data-testid="user-manual-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen size={24} className="text-[#D35400]" />
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">User Manual</h1>
            <p className="text-sm text-slate-500 mt-0.5">Comprehensive guide for Roofs To Remedy CRM</p>
          </div>
        </div>
        <button
          data-testid="download-manual-pdf"
          onClick={downloadPdf}
          disabled={downloading}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D35400] text-white rounded-lg text-sm font-semibold hover:bg-[#A04000] transition-all disabled:opacity-50"
        >
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {downloading ? 'Generating PDF...' : 'Download Full Manual (PDF)'}
        </button>
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-xl p-5">
        <p className="text-sm text-slate-600 mb-4">
          This manual covers every page in the CRM. Click any section to expand its documentation, or download the complete manual as a PDF.
          You can also access page-specific help by clicking the <span className="inline-flex items-center gap-0.5 text-[#D35400] font-semibold">? help icon</span> in the top-right corner of any page.
        </p>

        <div className="space-y-1">
          {SECTIONS.map(([key, page]) => (
            <div key={key} className="border border-[#E2E2DF] rounded-lg overflow-hidden">
              <button
                data-testid={`manual-section-${key.replace(/\//g, '-')}`}
                onClick={() => toggle(key)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expanded === key ? <ChevronDown size={16} className="text-[#D35400]" /> : <ChevronRight size={16} className="text-slate-400" />}
                  <span className="text-sm font-semibold text-slate-800">{page.title}</span>
                  <span className="text-xs text-slate-400">{key}</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(key); }}
                  className="text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-500 hover:bg-[#D35400]/10 hover:text-[#D35400] transition-colors font-medium"
                >
                  Go to page
                </button>
              </button>

              {expanded === key && (
                <div className="px-5 pb-5 pt-2 border-t border-[#E2E2DF] space-y-4 bg-slate-50/50">
                  <section>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#D35400] mb-1.5">Summary</h4>
                    <p className="text-sm text-slate-700 leading-relaxed">{page.summary}</p>
                  </section>
                  <section>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#D35400] mb-1.5">How To Use</h4>
                    <ol className="space-y-1.5">
                      {page.howTo.map((step, i) => (
                        <li key={i} className="flex gap-2 text-sm text-slate-700">
                          <span className="flex-shrink-0 w-4 h-4 rounded-full bg-[#D35400]/10 text-[#D35400] text-[9px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                          <span className="leading-relaxed">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </section>
                  <section>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#D35400] mb-1.5">Detailed Breakdown</h4>
                    <div className="space-y-1">
                      {page.detailed.split('\n').map((line, i) => {
                        const trimmed = line.trim();
                        if (!trimmed) return <div key={i} className="h-1.5" />;
                        if (trimmed.match(/^[A-Z ]{4,}[:(]/) || trimmed.match(/^[A-Z ]+:$/)) {
                          return <p key={i} className="text-xs font-bold text-slate-800 mt-1.5">{trimmed}</p>;
                        }
                        if (trimmed.includes('=') || trimmed.includes('\u00d7') || trimmed.includes('\u2192')) {
                          return <p key={i} className="text-xs text-slate-700 font-mono bg-white rounded px-2 py-0.5 leading-relaxed">{trimmed}</p>;
                        }
                        return <p key={i} className="text-sm text-slate-600 leading-relaxed">{trimmed}</p>;
                      })}
                    </div>
                  </section>
                  {page.relatedPages && page.relatedPages.length > 0 && (
                    <section>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-[#D35400] mb-1.5">Related Pages</h4>
                      <div className="flex flex-wrap gap-2">
                        {page.relatedPages.map(rp => {
                          const rPage = MANUAL[rp];
                          if (!rPage) return null;
                          return (
                            <button key={rp} onClick={() => { toggle(rp); document.querySelector(`[data-testid="manual-section-${rp.replace(/\//g, '-')}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#E2E2DF] text-slate-600 hover:border-[#D35400] hover:text-[#D35400] transition-colors">
                              {rPage.title} <ArrowRight size={10} />
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
